import { rootStore } from '../grid/reduxStore/Store';
import { DEFAULT_BLOCK_BORDER_COLOR } from '../shared/documentColorDefaults';
import { CommandBase } from './CommandBase';
import type { CommandBaseDependencies } from './CommandBase';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { defaultBlockStyle } from '../hooks/useBlockSettings.ts';
import type { BlockAddedData, BlockEmbedAddedData, BlockTableAddedData, SocketBlockSettings } from '../grid/reduxStore/saveHandlers.ts';
import { buildBlockAddedPayload, buildBlockEmbedAddedPayload, buildBlockTableAddedPayload } from '../grid/reduxStore/saveHandlers.ts';
import type { GridDeletedSocketResponseType } from '../grid/reduxStore/gridBlockSocketActions.ts';
import { deleteGridBlockState, selectSectionById, setMaxHeightPage } from '../grid/reduxStore/editorSlice.ts';
import { EditorDefaultSidePanelType, setActiveSidePanel } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { calculatePageMaxHeight } from '../GridDndEditor/gridHelper.tsx';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents.tsx';
import { GridBlockType } from 'services/entities/BlockTypes';
import { serializeBlockContent } from './serializeBlockContent';

export type BlockCommandDependencies = CommandBaseDependencies & {
  userId: number;
};

export abstract class BlockBaseCommand extends CommandBase {
  readonly scope = 'block' as const;
  protected readonly userId: number;

  constructor(dependencies: BlockCommandDependencies) {
    super(dependencies);
    this.userId = dependencies.userId;
  }

  abstract getCurrentBlockId(): string;

  protected buildBlockPayloadForSocket(blockSnapshot, blockId, sectionId): BlockAddedData | BlockTableAddedData | BlockEmbedAddedData {
    if (blockSnapshot.type === GridBlockType.SIGNATURE) {
      throw new Error('SIGNATURE blocks are not yet supported in WebSocket events');
    }

    const isTable = blockSnapshot.type === GridBlockType.TABLE;

    if (isTable) {
      if (!blockSnapshot.contentTable) {
        throw new Error('Table block requires contentTable');
      }

      return buildBlockTableAddedPayload({
        blockId: blockId,
        userId: this.userId,
        content: blockSnapshot.contentTable,
        position: {
          top_px: blockSnapshot.blockConfig.y,
          left_px: blockSnapshot.blockConfig.x,
        },
        dimensions: {
          width_px: blockSnapshot.blockConfig.width,
          height_px: blockSnapshot.blockConfig.height,
        },
        sectionId: sectionId,
        zIndex: blockSnapshot.blockConfig.z,
      });
    }

    if (blockSnapshot.type === GridBlockType.EMBED) {
      return buildBlockEmbedAddedPayload({
        blockId,
        userId: this.userId,
        sectionId,
        position: {
          top_px: blockSnapshot.blockConfig.y,
          left_px: blockSnapshot.blockConfig.x,
        },
        dimensions: {
          width_px: blockSnapshot.blockConfig.width,
          height_px: blockSnapshot.blockConfig.height,
        },
        zIndex: blockSnapshot.blockConfig.z,
      });
    }

    const htmlContent = serializeBlockContent(blockSnapshot.content);

    return buildBlockAddedPayload({
      blockId: blockId,
      userId: this.userId,
      type: blockSnapshot.type as 'TEXT' | 'IMAGE' | 'VIDEO' | 'SHAPE',
      htmlContent,
      position: {
        top_px: blockSnapshot.blockConfig.y,
        left_px: blockSnapshot.blockConfig.x,
      },
      dimensions: {
        width_px: blockSnapshot.blockConfig.width,
        height_px: blockSnapshot.blockConfig.height,
      },
      sectionId: sectionId,
      zIndex: blockSnapshot.blockConfig.z,
      ...(blockSnapshot.styles && {
        styles: this.cleanStylesForPayload(blockSnapshot.styles, blockId),
      }),
    });
  }

  protected cleanStylesForPayload(styles = defaultBlockStyle, newBlockId: string): SocketBlockSettings {
    const { ...cleanStyles } = styles;

    return {
      blockId: newBlockId,
      updatedByUserId: cleanStyles.updatedByUserId,
      currentTimestamp: new Date().toISOString(),
      borderLeft: cleanStyles.borderLeft ?? 0,
      borderRight: cleanStyles.borderRight ?? 0,
      borderTop: cleanStyles.borderTop ?? 0,
      borderBottom: cleanStyles.borderBottom ?? 0,
      borderRadius: cleanStyles.borderRadius ?? 0,
      paddingLeft: cleanStyles.paddingLeft ?? 0,
      paddingRight: cleanStyles.paddingRight ?? 0,
      paddingTop: cleanStyles.paddingTop ?? 0,
      paddingBottom: cleanStyles.paddingBottom ?? 0,
      borderColor: cleanStyles.borderColor ?? DEFAULT_BLOCK_BORDER_COLOR,
      ...(cleanStyles.backgroundColor !== undefined && {
        backgroundColor: cleanStyles.backgroundColor,
      }),
      ...(cleanStyles.opacity !== undefined && {
        opacity: cleanStyles.opacity,
      }),
      ...(cleanStyles.imageAlt !== undefined && {
        imageAlt: cleanStyles.imageAlt,
      }),
      ...(cleanStyles.imageLink !== undefined && {
        imageLink: cleanStyles.imageLink,
      }),
      ...(cleanStyles.imageAspectRatioLock !== undefined && {
        imageAspectRatioLock: cleanStyles.imageAspectRatioLock,
      }),
      ...(cleanStyles.lockedBy !== undefined && {
        lockedBy: cleanStyles.lockedBy,
      }),
      ...(cleanStyles.top !== undefined && {
        top: cleanStyles.top,
      }),
    };
  }

  protected handleDeleteSuccess(
    sectionId: string,
    blockId: string,
    status: GridDeletedSocketResponseType['status'],
    errorCode: GridDeletedSocketResponseType['errorCode'],
    getSignaturesBySectionId?: (sectionId: string) => SignatureBox[]
  ) {
    this.dispatch(setSaveStatusBasedOnApiResponse({ status, errorCode }));
    if (status !== AcknowledgmentResponseStatus.OK) {
      return;
    }
    this.dispatch(
      deleteGridBlockState({
        sectionId,
        blockId,
      })
    );
    this.dispatch(
      setActiveSidePanel({
        type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL,
      })
    );

    this.recalculatePageHeight(sectionId, getSignaturesBySectionId);
  }

  protected recalculatePageHeight(sectionId: string, getSignaturesBySectionId?: (sectionId: string) => SignatureBox[]): void {
    const state = rootStore.getState();
    const section = selectSectionById(state, sectionId);

    if (!section) return;

    const blocksContent = section.blocksContent || {};

    const signatures = getSignaturesBySectionId
      ? getSignaturesBySectionId(sectionId)
      : Object.values(blocksContent)
          .filter((block) => block.type === GridBlockType.SIGNATURE && block.contentSignature)
          .map((block) => block.contentSignature as SignatureBox);

    const maxHeightPage = calculatePageMaxHeight(blocksContent, signatures);

    this.dispatch(
      setMaxHeightPage({
        maxHeightPage,
        sectionId,
      })
    );
  }
}
